import React, { useEffect, useState } from 'react'
import { Box, Card } from '@mui/material'
import {
  Typography,
  Button,
  Grid,
  CircularProgress,
  makeStyles,
  Divider,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton
} from '@material-ui/core'
import {
  ArrowDropDown as ArrowDropDownIcon,
  SendOutlined as SendIcon,
  ScheduleOutlined as ScheduleIcon,
  GroupOutlined as GroupIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Close as CloseIcon
} from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'utils/notify'
import ConfirmModal from 'components/ConfirmationModal'
import {
  addAnnouncement,
  fetchAnnouncementById,
  resetAnnouncement,
  sendAnnouncement,
  updateAnnouncementById
} from 'store/announcements'
import { Form, Formik } from 'formik'
import { announcementFormSchema } from 'schemas/announcement'
import { isEditorContentEmpty } from 'schemas/setting'
import { isEmpty } from 'lodash'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import CustomBox from 'components/Form/CustomBox'
import CustomDateTimePicker from 'components/CustomDateTimePicker'
import { AnnouncementStatus } from 'constants/index'
import { getDefaultTimezone } from 'utils/times'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import ErrorMessage from 'components/ErrorMessage'
import DataLoading from 'components/DataLoading'

export const recipientsObject = {
  ATTENDEE: 'Attendee',
  SPEAKER: 'Speaker',
  MODERATOR: 'Moderator',
  EXHIBITOR: 'Exhibitor',
  SPONSOR: 'Sponsor'
}

const recipients = Object.values(recipientsObject).map((value) => value)

// Matches the Cloud Tasks queue's maximum schedule delay used by the backend to send
// scheduled announcements.
const MAX_SCHEDULE_DAYS = 30

const AnnouncementForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const { announcementId } = useParams()
  const [scheduledProgress, setScheduledProgress] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [performingAction, setPerformingAction] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  // Holds the validated payload awaiting the user's "send now" confirmation.
  const [pendingSend, setPendingSend] = useState(null)

  const { conference } = useSelector((state) => state.conference)
  const { announcement } = useSelector((state) => state.announcements)

  const isSentScheduled =
    announcementId && announcement?.scheduledOn && announcement?.status === AnnouncementStatus.SENT
  // An already-sent announcement (whether it was scheduled or immediate) is read-only
  // for the send/schedule controls — you can only edit its text/recipients.
  const isAlreadySent = announcementId && announcement?.status === AnnouncementStatus.SENT
  // Editing a still-pending scheduled announcement: keep the schedule editable.
  const isEditingPending =
    announcementId && announcement?.scheduledOn && announcement?.status !== AnnouncementStatus.SENT

  const initialValues = {
    announcementText: '',
    isScheduled: false,
    scheduledOn: null,
    status: AnnouncementStatus.SCHEDULED,
    // New announcements default to every recipient group selected.
    recipients: [...recipients]
  }

  if (announcement && announcement.id === announcementId) {
    initialValues.announcementText = announcement.announcementText
    if (announcement.scheduledOn) {
      initialValues.scheduledOn = moment(announcement.scheduledOn).tz(conference?.timezone)
    }
    initialValues.status = announcement.status
    initialValues.recipients = announcement?.recipients ?? []
    initialValues.isScheduled =
      announcement.status === AnnouncementStatus.SCHEDULED &&
      announcement.scheduledOn &&
      moment(announcement.scheduledOn).isAfter(moment())
  }

  const goBack = () => {
    dispatch(resetAnnouncement())
    history.goBack()
  }

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true)
      await dispatch(fetchAnnouncementById(announcementId))
      setIsLoading(false)
    }

    if (announcementId) {
      fetch()
    }
  }, [announcementId])

  const getMinimumDate = (providedDate, timezone) => {
    const currentDate = moment().tz(timezone)
    if (currentDate.isAfter(moment(providedDate).tz(timezone))) {
      return currentDate
    } else {
      return moment(providedDate).tz(timezone)
    }
  }
  const getMaxDate = (endDate, timezone) => {
    const utcTime = moment.utc(endDate)
    const time = utcTime.tz(timezone).format('YYYY-MM-DD')
    const confMaxDate = moment.tz(time, timezone)
    // Cloud Tasks (used by the backend to send scheduled announcements) can't
    // schedule a task more than 30 days out.
    const scheduleLimit = moment().tz(timezone).add(MAX_SCHEDULE_DAYS, 'days')
    return confMaxDate.isBefore(scheduleLimit) ? confMaxDate : scheduleLimit
  }

  const toggleRecipient = (selected, value, setFieldValue) => {
    const next = selected.includes(value)
      ? selected.filter((r) => r !== value)
      : [...selected, value]
    setFieldValue('recipients', next)
  }

  const handleSelectAll = (selectedRecipients, setFieldValue) => {
    if (selectedRecipients.length > 0 && selectedRecipients.length === recipients.length) {
      setFieldValue('recipients', [])
    } else {
      setFieldValue('recipients', [...recipients])
    }
  }

  const onSubmit = async ({ announcementText, isScheduled, scheduledOn, recipients }) => {
    let scheduledTimeInUTC = null
    if (isScheduled && scheduledOn) {
      scheduledTimeInUTC = moment(scheduledOn).utc()
      scheduledTimeInUTC.seconds(0)
      scheduledTimeInUTC.milliseconds(0)
    }

    const nowUTC = moment.utc().valueOf()

    const payload = {
      status: isScheduled ? AnnouncementStatus.SCHEDULED : AnnouncementStatus.SENT,
      announcementText: announcementText.trim(),
      // Stamped by the backend once the notification is actually sent (immediate
      // or scheduled) — kept explicitly null rather than omitted, since the
      // fetchAnnouncements listener orders by this field and Firestore drops
      // documents missing an orderBy field entirely.
      publishedOn: null,
      scheduledOn: isScheduled && scheduledTimeInUTC ? scheduledTimeInUTC.valueOf() : '',
      recipients,
      createdAt: nowUTC,
      updatedAt: nowUTC
    }
    if (isSentScheduled) {
      payload.status = AnnouncementStatus.SENT
    }

    // Immediate send of a new announcement asks for confirmation first.
    if (!announcementId && !isScheduled) {
      setPendingSend(payload)
      return
    }

    setPerformingAction(true)
    if (announcementId) {
      payload.publishedOn = announcement?.publishedOn
      // Preserve the original creation timestamp on edits.
      payload.createdAt = announcement?.createdAt
      await dispatch(updateAnnouncementById(announcementId, payload))
      toast.success(`Announcement updated`)
    } else {
      // Scheduled new announcement.
      setScheduledProgress(true)
      await dispatch(addAnnouncement(payload))
      setScheduledProgress(false)
      toast.success(`Scheduled Announcement created`)
    }
    setPerformingAction(false)
    goBack()
  }

  // Runs after the user confirms an immediate send in the modal.
  const handleConfirmSend = async () => {
    if (!pendingSend) return
    setPerformingAction(true)
    const newAnnouncementId = await dispatch(addAnnouncement(pendingSend))
    if (newAnnouncementId) {
      dispatch(sendAnnouncement(newAnnouncementId))
    }
    toast.success(`Announcement created`)
    setPerformingAction(false)
    setPendingSend(null)
    goBack()
  }

  if (isLoading) {
    return (
      <Box style={{ marginTop: '2rem' }}>
        <DataLoading />
      </Box>
    )
  }

  const minDate =
    !isEmpty(conference) && getMinimumDate(conference?.startDate, conference?.timezone)
  const maxDate = !isEmpty(conference) && getMaxDate(conference?.endDate, conference?.timezone)

  return (
    <CustomBox>
      <ViewHeader title={announcementId ? 'Edit Announcement' : 'Create Announcement'}>
        <div style={{ marginRight: 10 }}>
          <ActionBar actionText="Back" onAction={() => goBack()} />
        </div>
      </ViewHeader>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={announcementFormSchema}
        onSubmit={onSubmit}>
        {({ values, setFieldValue, errors, touched, setFieldTouched, submitForm }) => {
          const recipientCount = values.recipients?.length || 0
          const allRecipientsSelected = recipientCount === recipients.length
          // Gate the send/schedule actions until there's text and at least one recipient.
          const canSubmit = !isEditorContentEmpty(values.announcementText) && recipientCount > 0

          const markSendNow = () => {
            setFieldValue('isScheduled', false)
            setFieldValue('status', AnnouncementStatus.SENT)
          }

          const markScheduled = () => {
            setFieldValue('isScheduled', true)
            setFieldValue(
              'status',
              isSentScheduled ? AnnouncementStatus.SENT : AnnouncementStatus.SCHEDULED
            )
          }

          const handleScheduleConfirm = async () => {
            setFieldTouched('scheduledOn', true)
            // Validate just the schedule field before committing.
            if (!values.scheduledOn) {
              return
            }
            markScheduled()
            setScheduleOpen(false)
            // Allow Formik state (isScheduled/status) to settle before submitting.
            setTimeout(() => submitForm(), 0)
          }

          return (
            <Form>
              <Grid container spacing={3}>
                {/* Main column — composer */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <label className={classes.label}>Announcement Text</label>
                    <TipTapEditor
                      value={values.announcementText}
                      onChange={(value) => setFieldValue('announcementText', value)}
                      placeholder="Write your announcement here..."
                      error={!!(touched?.announcementText && errors?.announcementText)}
                    />
                    <ErrorMessage message={touched?.announcementText && errors?.announcementText} />
                  </Box>
                </Grid>

                {/* Sidebar — audience + summary */}
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" className={classes.sideCard}>
                    <Box className={classes.sideHeaderRow}>
                      <Box className={classes.sideHeader}>
                        <GroupIcon fontSize="small" className={classes.sideHeaderIcon} />
                        <Typography className={classes.sideTitle}>Audience</Typography>
                      </Box>
                      <Box
                        className={classes.selectAllBtn}
                        onClick={() => handleSelectAll(values.recipients, setFieldValue)}>
                        {allRecipientsSelected ? (
                          <CheckBoxOutlineBlankIcon className={classes.selectAllIcon} />
                        ) : (
                          <CheckBoxOutlinedIcon className={classes.selectAllIcon} />
                        )}
                        {allRecipientsSelected ? 'Unselect all' : 'Select all'}
                      </Box>
                    </Box>
                    <Typography className={classes.sideHint}>
                      Choose who should receive this announcement.
                    </Typography>

                    <Box className={classes.recipientGrid}>
                      {Object.entries(recipientsObject).map(([key, value]) => {
                        const isSelected = values.recipients.includes(value)
                        return (
                          <Box
                            key={key}
                            className={`${classes.recipientPill} ${
                              isSelected ? classes.recipientPillActive : ''
                            }`}
                            onClick={() => {
                              toggleRecipient(values.recipients, value, setFieldValue)
                              setFieldTouched('recipients', true)
                            }}>
                            {isSelected && <span className={classes.recipientCheck}>✓</span>}
                            <Typography className={classes.recipientLabel}>{value}</Typography>
                          </Box>
                        )
                      })}
                    </Box>
                    {touched.recipients && errors.recipients && (
                      <ErrorMessage message={errors.recipients} />
                    )}

                    <Divider className={classes.sideDivider} />

                    <Box className={classes.summaryRow}>
                      <GroupIcon fontSize="small" className={classes.summaryIcon} />
                      <Typography className={classes.summaryText}>
                        {recipientCount === 0
                          ? 'No recipients selected'
                          : recipientCount === recipients.length
                          ? 'Everyone in the conference'
                          : `${recipientCount} recipient group${recipientCount > 1 ? 's' : ''}`}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>

              {/* Footer — actions */}
              <Divider className={classes.footerDivider} />
              <Box className={classes.footer}>
                <Button
                  className={classes.cancelButton}
                  onClick={goBack}
                  disabled={performingAction || scheduledProgress}>
                  Cancel
                </Button>

                {isAlreadySent ? (
                  // Already-sent announcement: only "Save" the edited text/recipients.
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.primaryButton}
                    disabled={performingAction || !canSubmit}
                    onClick={markScheduled}>
                    {performingAction && (
                      <CircularProgress size={18} color="inherit" className={classes.btnSpinner} />
                    )}
                    Save Changes
                  </Button>
                ) : isEditingPending ? (
                  // Editing a still-pending scheduled announcement.
                  <SplitSendButton
                    classes={classes}
                    scheduleOpen={scheduleOpen}
                    setScheduleOpen={setScheduleOpen}
                    performingAction={performingAction}
                    scheduledProgress={scheduledProgress}
                    canSubmit={canSubmit}
                    onSendNow={markSendNow}
                    scheduledOn={values.scheduledOn}
                    setScheduledOn={(v) => setFieldValue('scheduledOn', v)}
                    error={touched.scheduledOn && errors.scheduledOn}
                    onScheduleConfirm={handleScheduleConfirm}
                    conference={conference}
                    minDate={minDate}
                    maxDate={maxDate}
                    confirmLabel="Update Schedule"
                  />
                ) : (
                  // Creating a new announcement.
                  <SplitSendButton
                    classes={classes}
                    scheduleOpen={scheduleOpen}
                    setScheduleOpen={setScheduleOpen}
                    performingAction={performingAction}
                    scheduledProgress={scheduledProgress}
                    canSubmit={canSubmit}
                    onSendNow={markSendNow}
                    scheduledOn={values.scheduledOn}
                    setScheduledOn={(v) => setFieldValue('scheduledOn', v)}
                    error={touched.scheduledOn && errors.scheduledOn}
                    onScheduleConfirm={handleScheduleConfirm}
                    conference={conference}
                    minDate={minDate}
                    maxDate={maxDate}
                    confirmLabel="Schedule"
                  />
                )}
              </Box>
            </Form>
          )
        }}
      </Formik>

      <ConfirmModal
        dialogTitle="Send Announcement"
        dialogText="Are you sure you want to send this announcement now?"
        open={!!pendingSend}
        inProgress={performingAction}
        closeModal={() => setPendingSend(null)}
        handleConfirm={handleConfirmSend}
      />
    </CustomBox>
  )
}

/**
 * Split "Send Now" button: primary action sends immediately; the caret opens a
 * popover to schedule for later — the familiar Slack-style pattern.
 */
const SplitSendButton = ({
  classes,
  scheduleOpen,
  setScheduleOpen,
  performingAction,
  scheduledProgress,
  canSubmit,
  onSendNow,
  scheduledOn,
  setScheduledOn,
  error,
  onScheduleConfirm,
  conference,
  minDate,
  maxDate,
  confirmLabel
}) => {
  const busy = performingAction || scheduledProgress
  // A chosen time before the earliest allowed (e.g. in the past) is invalid even
  // before the field is "touched" — surface a message so the disabled button is explained.
  const isPastTime = scheduledOn && minDate && moment(scheduledOn).isBefore(moment(minDate))
  const scheduleError = error || (isPastTime ? 'Schedule time must be in the future.' : '')
  // Block the schedule action when there's no date or the time is invalid.
  const scheduleInvalid = !scheduledOn || !!scheduleError

  return (
    <>
      <ButtonGroup
        variant="contained"
        color="primary"
        disabled={busy || !canSubmit}
        className={classes.sendGroup}>
        <Button
          type="submit"
          color="primary"
          className={classes.sendButton}
          onClick={onSendNow}
          startIcon={
            performingAction && !scheduledProgress ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon fontSize="small" />
            )
          }>
          Send Now
        </Button>
        <Button
          color="primary"
          size="small"
          className={classes.sendCaret}
          onClick={() => setScheduleOpen(true)}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>

      <Dialog
        open={scheduleOpen}
        onClose={() => !busy && setScheduleOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.scheduleDialog }}>
        <Box className={classes.scheduleHeader}>
          <Box className={classes.scheduleHeaderLeft}>
            <ScheduleIcon fontSize="small" className={classes.scheduleHeaderIcon} />
            <Box>
              <Typography className={classes.scheduleTitle}>Schedule announcement</Typography>
              <Typography className={classes.scheduleSubtitle}>
                Time zone: {getDefaultTimezone(conference?.timezone)}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => setScheduleOpen(false)}
            disabled={busy}
            className={classes.scheduleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent className={classes.scheduleBody}>
          <CustomDateTimePicker
            mode="datetime"
            value={scheduledOn}
            label="Date & time"
            name="scheduledOn"
            is24hrFormat={conference?.is24hrFormat || false}
            minDate={minDate}
            maxDate={maxDate}
            onChange={setScheduledOn}
            error={!!scheduleError}
            helperText={scheduleError}
          />
          {!scheduleError && (
            <Typography className={classes.scheduleHint}>
              Announcements can be scheduled up to {MAX_SCHEDULE_DAYS} days in advance.
            </Typography>
          )}
        </DialogContent>

        <DialogActions className={classes.scheduleFooter}>
          <Button onClick={() => setScheduleOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.scheduleConfirm}
            disabled={busy || scheduleInvalid}
            onClick={onScheduleConfirm}
            startIcon={
              scheduledProgress ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ScheduleIcon fontSize="small" />
              )
            }>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AnnouncementForm

const useStyles = makeStyles((theme) => ({
  label: {
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.4px',
    color: '#606060'
  },
  // Sidebar
  sideCard: {
    padding: '1.25rem',
    borderRadius: '12px',
    borderColor: '#E6E8EB',
    backgroundColor: '#FBFCFD'
  },
  sideHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  sideHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  sideHeaderIcon: {
    color: theme.palette.primary.main
  },
  sideTitle: {
    fontWeight: 600,
    fontSize: '15px',
    color: '#2b2b2b'
  },
  selectAllBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#666',
    cursor: 'pointer',
    userSelect: 'none',
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: '#f0fbfd'
    }
  },
  selectAllIcon: {
    fontSize: '15px !important'
  },
  sideHint: {
    fontSize: '12.5px',
    color: '#8a8f96',
    margin: '4px 0 14px 0'
  },
  recipientGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  recipientPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 14px',
    borderRadius: '20px',
    border: '1.5px solid #e0e0e0',
    backgroundColor: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: '#f0fbfd'
    }
  },
  recipientPillActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#f0fbfd'
  },
  recipientCheck: {
    fontSize: '11px',
    fontWeight: 700,
    color: theme.palette.primary.main,
    lineHeight: 1
  },
  recipientLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333'
  },
  sideDivider: {
    margin: '16px 0'
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  },
  summaryIcon: {
    color: '#9aa0a6'
  },
  summaryText: {
    fontSize: '12.5px',
    color: '#5f6368'
  },
  // Footer
  footerDivider: {
    margin: '1.75rem 0 1rem 0'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  cancelButton: {
    textTransform: 'none',
    fontWeight: 500,
    color: '#5f6368'
  },
  primaryButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    padding: '8px 22px'
  },
  btnSpinner: {
    marginRight: 8
  },
  // Split send button
  sendGroup: {
    borderRadius: '8px'
  },
  sendButton: {
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 22px'
  },
  sendCaret: {
    padding: '6px 4px',
    minWidth: '40px'
  },
  // Schedule dialog
  scheduleDialog: {
    borderRadius: '14px'
  },
  scheduleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '18px 20px 14px',
    borderBottom: '1px solid #EFF1F3'
  },
  scheduleHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  scheduleHeaderIcon: {
    color: theme.palette.primary.main
  },
  scheduleTitle: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#1a1a1a'
  },
  scheduleSubtitle: {
    fontSize: '12px',
    color: '#8a8f96'
  },
  scheduleClose: {
    color: '#9aa0a6'
  },
  scheduleBody: {
    padding: '20px !important'
  },
  scheduleHint: {
    fontSize: '12px',
    color: '#8a8f96',
    marginTop: '8px'
  },
  scheduleFooter: {
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '12px 20px 18px'
  },
  scheduleConfirm: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    padding: '6px 18px'
  }
}))
